import colors from '@utils/colors';
import styled, { keyframes } from 'styled-components';

/**
 * A short loop for What's New: one session dragged down a day, the next two pushed along, a toast, then back to the
 * start. It copies the calendar's real drag (calendarStyles' body.bumpers-drag): bumper ends on the sessions that can
 * move, none on a fixed event, and a thick dashed outline on the one being dragged.
 *
 * Moved by the browser's own animation code only (transform and opacity). With reduced motion it shows the finished
 * still: every element's own style is the end state, and the animations only run on top of it.
 */

const LOOP_MS = 8000;
const PRESS_MS = 1050;
const DRAG_START_MS = 1250;
const DROP_MS = 3250;
const CURSOR_IN_MS = [250, 1000];
const CURSOR_OUT_MS = [3650, 4300];
const TOAST_IN_MS = 3500;
const TOAST_OUT_MS = 6350;
const FADE_OUT_MS = 6900;
const FADE_IN_MS = 7450;
const FADE_LENGTH_MS = 300;
const QUICK_MS = 150;

const HOUR_PX = 44;
const HEADER_PX = 40;
const FOOTER_PX = 38;
const GUTTER_PX = 44;
const FRAME_WIDTH_PX = 300;
const DAY_HEIGHT_PX = HOUR_PX * 6;
const FRAME_HEIGHT_PX = HEADER_PX + DAY_HEIGHT_PX + FOOTER_PX;

// Where each block starts, in minutes after 1pm. Five minutes' break stays between sessions, as the real push keeps it.
const toPx = (minutes: number) => (minutes * HOUR_PX) / 60;
const DRAG_DISTANCE_PX = toPx(120);
const SECOND_TOP_PX = toPx(120);
const THIRD_TOP_PX = toPx(190);
const BLOCK_PX = toPx(60);
const BREAK_PX = toPx(5);

const pushedBySecond = (dragged: number) => Math.max(0, dragged + BLOCK_PX + BREAK_PX - SECOND_TOP_PX);
const pushedByThird = (dragged: number) =>
  Math.max(0, SECOND_TOP_PX + pushedBySecond(dragged) + BLOCK_PX + BREAK_PX - THIRD_TOP_PX);

const smooth = (p: number) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);
const at = (ms: number) => `${((ms / LOOP_MS) * 100).toFixed(2)}%`;
const line = (ms: number, css: string) => `${at(ms)} { ${css} }`;

const DRAG_STEPS = 24;
const dragSteps = Array.from({ length: DRAG_STEPS + 1 }, (_, step) => step / DRAG_STEPS);

/** A block's slide: sits still, follows the drag down with the rest in lockstep, holds, jumps home under the fade. */
const slide = (offset: (dragged: number) => number) => {
  const end = offset(DRAG_DISTANCE_PX);
  return keyframes`
    ${line(0, 'transform: translateY(0)')}
    ${dragSteps
      .map(p =>
        line(
          DRAG_START_MS + p * (DROP_MS - DRAG_START_MS),
          `transform: translateY(${offset(DRAG_DISTANCE_PX * smooth(p)).toFixed(2)}px)`
        )
      )
      .join('\n')}
    ${line(FADE_IN_MS - 1, `transform: translateY(${end}px)`)}
    ${line(FADE_IN_MS, 'transform: translateY(0)')}
    ${line(LOOP_MS, 'transform: translateY(0)')}
  `;
};

const draggedSlide = slide(dragged => dragged);
const secondSlide = slide(pushedBySecond);
const thirdSlide = slide(pushedByThird);

// Shown from the press to the drop.
const whileDragging = keyframes`
  ${line(0, 'opacity: 0')}
  ${line(PRESS_MS, 'opacity: 0')}
  ${line(PRESS_MS + QUICK_MS, 'opacity: 1')}
  ${line(DROP_MS, 'opacity: 1')}
  ${line(DROP_MS + QUICK_MS, 'opacity: 0')}
  ${line(LOOP_MS, 'opacity: 0')}
`;
const exceptWhileDragging = keyframes`
  ${line(0, 'opacity: 1')}
  ${line(PRESS_MS, 'opacity: 1')}
  ${line(PRESS_MS + QUICK_MS, 'opacity: 0')}
  ${line(DROP_MS, 'opacity: 0')}
  ${line(DROP_MS + QUICK_MS, 'opacity: 1')}
  ${line(LOOP_MS, 'opacity: 1')}
`;

// The times on a block change as it lands; home again under the fade.
const timeBefore = keyframes`
  ${line(0, 'opacity: 1')}
  ${line(DROP_MS, 'opacity: 1')}
  ${line(DROP_MS + QUICK_MS, 'opacity: 0')}
  ${line(FADE_IN_MS - 1, 'opacity: 0')}
  ${line(FADE_IN_MS, 'opacity: 1')}
  ${line(LOOP_MS, 'opacity: 1')}
`;
const timeAfter = keyframes`
  ${line(0, 'opacity: 0')}
  ${line(DROP_MS, 'opacity: 0')}
  ${line(DROP_MS + QUICK_MS, 'opacity: 1')}
  ${line(FADE_IN_MS - 1, 'opacity: 1')}
  ${line(FADE_IN_MS, 'opacity: 0')}
  ${line(LOOP_MS, 'opacity: 0')}
`;

const dayFade = keyframes`
  ${line(0, 'opacity: 1')}
  ${line(FADE_OUT_MS, 'opacity: 1')}
  ${line(FADE_OUT_MS + FADE_LENGTH_MS, 'opacity: 0')}
  ${line(FADE_IN_MS, 'opacity: 0')}
  ${line(FADE_IN_MS + FADE_LENGTH_MS, 'opacity: 1')}
  ${line(LOOP_MS, 'opacity: 1')}
`;

const toastInOut = keyframes`
  ${line(0, 'opacity: 0; transform: translateY(10px)')}
  ${line(TOAST_IN_MS, 'opacity: 0; transform: translateY(10px)')}
  ${line(TOAST_IN_MS + 220, 'opacity: 1; transform: translateY(0)')}
  ${line(TOAST_OUT_MS, 'opacity: 1; transform: translateY(0)')}
  ${line(TOAST_OUT_MS + 220, 'opacity: 0; transform: translateY(10px)')}
  ${line(LOOP_MS, 'opacity: 0; transform: translateY(10px)')}
`;

// The cursor arrives over the first block, presses, carries it down, lets go and leaves.
const CURSOR_HOME = { x: FRAME_WIDTH_PX + 20, y: FRAME_HEIGHT_PX - 30 };
const CURSOR_GRAB = { x: 176, y: HEADER_PX + 26 };
const cursorAt = (x: number, y: number, scale = 1) =>
  `transform: translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) scale(${scale})`;
const glideSteps = Array.from({ length: 13 }, (_, step) => step / 12);
const cursorMove = keyframes`
  ${line(0, cursorAt(CURSOR_HOME.x, CURSOR_HOME.y))}
  ${glideSteps
    .map(p =>
      line(
        CURSOR_IN_MS[0] + p * (CURSOR_IN_MS[1] - CURSOR_IN_MS[0]),
        cursorAt(
          CURSOR_HOME.x + (CURSOR_GRAB.x - CURSOR_HOME.x) * smooth(p),
          CURSOR_HOME.y + (CURSOR_GRAB.y - CURSOR_HOME.y) * smooth(p)
        )
      )
    )
    .join('\n')}
  ${line(PRESS_MS, cursorAt(CURSOR_GRAB.x, CURSOR_GRAB.y))}
  ${line(PRESS_MS + QUICK_MS, cursorAt(CURSOR_GRAB.x, CURSOR_GRAB.y, 0.86))}
  ${dragSteps
    .map(p =>
      line(
        DRAG_START_MS + p * (DROP_MS - DRAG_START_MS),
        cursorAt(CURSOR_GRAB.x, CURSOR_GRAB.y + DRAG_DISTANCE_PX * smooth(p), 0.86)
      )
    )
    .join('\n')}
  ${line(DROP_MS + QUICK_MS, cursorAt(CURSOR_GRAB.x, CURSOR_GRAB.y + DRAG_DISTANCE_PX))}
  ${glideSteps
    .map(p =>
      line(
        CURSOR_OUT_MS[0] + p * (CURSOR_OUT_MS[1] - CURSOR_OUT_MS[0]),
        cursorAt(
          CURSOR_GRAB.x + (CURSOR_HOME.x - CURSOR_GRAB.x) * smooth(p),
          CURSOR_GRAB.y + DRAG_DISTANCE_PX + (CURSOR_HOME.y - CURSOR_GRAB.y - DRAG_DISTANCE_PX) * smooth(p)
        )
      )
    )
    .join('\n')}
  ${line(LOOP_MS, cursorAt(CURSOR_HOME.x, CURSOR_HOME.y))}
`;

const MATH_COLOR = '#5E76FF';
const ENGLISH_COLOR = '#E91E63';
const BIOLOGY_COLOR = '#4CAF50';
const DINNER_COLOR = '#E0A800';

const HOURS = ['1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM'];

const Times = ({ before, after }: { before: string; after: string }) => (
  <TimeLine>
    <TimeBefore>{before}</TimeBefore>
    <TimeAfter>{after}</TimeAfter>
  </TimeLine>
);

const BumpersDemo = () => (
  <Frame role="img" aria-label="Dragging a study session down the calendar: the sessions in its way move along.">
    <Header>
      <Day>Thu 05</Day>
      <Switch>
        <Toggle />
        Bumpers
      </Switch>
    </Header>
    <DayArea>
      {HOURS.map((hour, index) => (
        <Hour key={hour} style={{ top: index * HOUR_PX }}>
          <HourLabel>{hour}</HourLabel>
        </Hour>
      ))}
      <Sessions>
        <Fixed style={{ top: toPx(315), height: toPx(45), color: DINNER_COLOR }}>
          <BlockFace />
          <BlockText>
            <TimeLine>
              <TimePlain>6:15 - 7pm</TimePlain>
            </TimeLine>
            <Course>Dinner</Course>
          </BlockText>
        </Fixed>
        <ThirdBlock style={{ top: THIRD_TOP_PX, height: BLOCK_PX, color: BIOLOGY_COLOR }}>
          <BlockFace />
          <BumperEnds />
          <BlockText>
            <Times before="4:10 - 5:10pm" after="5:10 - 6:10pm" />
            <Course>BIO 101</Course>
            <Task>Lab report 6</Task>
          </BlockText>
        </ThirdBlock>
        <SecondBlock style={{ top: SECOND_TOP_PX, height: BLOCK_PX, color: ENGLISH_COLOR }}>
          <BlockFace />
          <BumperEnds />
          <BlockText>
            <Times before="3 - 4pm" after="4:05 - 5:05pm" />
            <Course>ENGL 110</Course>
            <Task>Gatsby essay draft</Task>
          </BlockText>
        </SecondBlock>
        <DraggedBlock style={{ top: 0, height: BLOCK_PX, color: MATH_COLOR }}>
          <DraggedFace />
          <DashedOutline />
          <BlockText>
            <Times before="1 - 2pm" after="3 - 4pm" />
            <Course>MATH 152</Course>
            <Task>Problem set 9</Task>
          </BlockText>
        </DraggedBlock>
      </Sessions>
    </DayArea>
    <Toast>
      <span>Moved “Problem set 9” and 2 more</span>
      <Undo>Undo</Undo>
    </Toast>
    <Cursor viewBox="0 0 22 24" aria-hidden="true">
      <path
        d="M4 2.5 18 13l-6.4 1 3.7 7-2.8 1.4-3.6-7.2L4.4 20z"
        fill="#27303F"
        stroke="#FFFFFF"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </Cursor>
  </Frame>
);

export default BumpersDemo;

const Frame = styled.div`
  position: relative;
  flex: none;
  width: ${FRAME_WIDTH_PX}px;
  height: ${FRAME_HEIGHT_PX}px;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 10px;
  background: ${({ theme }) => theme.background};
  font-family: 'Quicksand', sans-serif;
  cursor: default;
  user-select: none;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${HEADER_PX}px;
  box-sizing: border-box;
  padding: 0 10px 0 ${GUTTER_PX + 4}px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
`;

const Day = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.primary};
`;

const Switch = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px 4px 7px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.textColor};
`;

const Toggle = styled.span`
  position: relative;
  width: 22px;
  height: 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.primary};

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    right: 2px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${colors.white};
  }
`;

const DayArea = styled.div`
  position: relative;
  height: ${DAY_HEIGHT_PX}px;
  animation: ${dayFade} ${LOOP_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Hour = styled.div`
  position: absolute;
  left: ${GUTTER_PX}px;
  right: 0;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  opacity: 0.6;
`;

const HourLabel = styled.span`
  position: absolute;
  top: -1px;
  left: -${GUTTER_PX - 6}px;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.textColor};
  white-space: nowrap;
`;

const Sessions = styled.div`
  position: absolute;
  top: 0;
  left: ${GUTTER_PX + 2}px;
  right: 8px;
  bottom: 0;
`;

const Block = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  border-radius: 4px;
`;

const Fixed = styled(Block)``;

const movingBlock = (name: ReturnType<typeof keyframes>, endPx: number) => styled(Block)`
  transform: translateY(${endPx}px);
  animation: ${name} ${LOOP_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const DraggedBlock = styled(movingBlock(draggedSlide, DRAG_DISTANCE_PX))`
  z-index: 2;
`;
const SecondBlock = movingBlock(secondSlide, pushedBySecond(DRAG_DISTANCE_PX));
const ThirdBlock = movingBlock(thirdSlide, pushedByThird(DRAG_DISTANCE_PX));

// A session at rest: a soft tint of its colour, a thin edge and a solid bar on the left.
const BlockFace = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 4px;
  border: 1px solid currentColor;
  border-left-width: 3px;
  background: currentColor;
  box-shadow: 0 1px 3px rgba(39, 48, 63, 0.14);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ theme }) => theme.background};
    opacity: 0.86;
  }
`;

const DraggedFace = styled(BlockFace)`
  animation: ${exceptWhileDragging} ${LOOP_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// During a drag: a thick end on the top and bottom of each session that can move.
const BumperEnds = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 6px;
  border-top: 3px solid currentColor;
  border-bottom: 3px solid currentColor;
  opacity: 0;
  animation: ${whileDragging} ${LOOP_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Where the dragged session will land: a thick border of short dashes over a faint tint.
const DashedOutline = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 6px;
  border: 2.5px dashed currentColor;
  opacity: 0;
  animation: ${whileDragging} ${LOOP_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const BlockText = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  padding: 3px 8px 2px 9px;
  overflow: hidden;
  line-height: 12.5px;
`;

const TimeLine = styled.div`
  position: relative;
  height: 12.5px;
  flex: none;
`;

const timeStyle = `
  position: absolute;
  top: 0;
  left: 0;
  font-size: 10.5px;
  font-weight: 700;
  color: currentColor;
  white-space: nowrap;
`;

const TimePlain = styled.span`
  ${timeStyle}
`;

const TimeBefore = styled.span`
  ${timeStyle}
  opacity: 0;
  animation: ${timeBefore} ${LOOP_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const TimeAfter = styled.span`
  ${timeStyle}
  animation: ${timeAfter} ${LOOP_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Course = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.textStrongColor};
  white-space: nowrap;
`;

const Task = styled.div`
  font-size: 10.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.textColor};
  white-space: nowrap;
`;

const Toast = styled.div`
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 10px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.name === 'dark' ? `color-mix(in srgb, ${theme.primary} 24%, ${theme.background})` : '#2E2E48'};
  color: ${colors.white};
  font-size: 10.5px;
  font-weight: 600;
  white-space: nowrap;
  animation: ${toastInOut} ${LOOP_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Undo = styled.span`
  font-weight: 700;
  color: ${({ theme }) => `color-mix(in srgb, ${theme.primary} 50%, ${colors.white})`};
`;

const Cursor = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 4;
  width: 22px;
  height: 24px;
  transform-origin: 4px 2px;
  transform: translate(${CURSOR_HOME.x}px, ${CURSOR_HOME.y}px);
  animation: ${cursorMove} ${LOOP_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
